// src/pages/ClusterBuilderPage.js

import React, { useState, useMemo, useRef, useEffect } from 'react';
import Draggable from 'react-draggable';
import axios from 'axios';
import * as htmlToImage from 'html-to-image';
import { v4 as uuidv4 } from 'uuid';
import { useDoorloopjes } from '../context/DoorloopjesContext';

const STRAPI_URL = process.env.REACT_APP_STRAPI_URL;

const HEXAGON_WIDTH = 240;
const HEXAGON_HEIGHT = 208;

const MIN_SCALE = 0.6;
const MAX_SCALE = 1.8;
const SCALE_STEP = 0.2;

const typeDisplayNames = {
  doorloopje: 'Doorloopje',
  why: 'Waarom',
  how: 'Hoe',
};

const DraggableHexagon = ({
  item,
  onRemove,
  onDragStop,
  onScaleChange,
  onSelect,
  isSelected,
}) => {
  const nodeRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);

  const imageUrl = item.steps?.[0]?.image?.url || item.image?.url;
  const bookNumber = item.book || item.book_number || '0';
  const pageNumber = item.page || item.page_number || '0';

  let hexagonColorClass = '';
  let footerText = '';

  switch (item.__type) {
    case 'why':
      hexagonColorClass = 'grey';
      footerText = 'WAAROM';
      break;
    case 'how':
      hexagonColorClass = 'grey';
      footerText = 'HOE';
      break;
    default:
      hexagonColorClass =
        item.theme?.title === 'Leesonderwijs' ? 'yellow' : 'blue';
      footerText = 'DOORLOOPJE';
      break;
  }

  return (
    <Draggable
      nodeRef={nodeRef}
      defaultPosition={{ x: item.x, y: item.y }}
      onStop={(e, data) => onDragStop(item.instanceId, data)}
      // deze elementen mogen géén drag starten
      cancel=".cb-hexagon-controls, .cb-hexagon-controls *, .cb-hexagon-remove-btn"
    >
      <div
        ref={nodeRef}
        className={`cb-draggable-container ${isSelected ? 'selected' : ''}`}
        data-instance-id={item.instanceId}
        style={{ position: 'absolute', zIndex: isHovered ? 100 : 'auto' }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onPointerDown={(e) => {
          if (e.isPrimary !== false) onSelect?.(item.instanceId);
        }}
      >
        <div
          className="cb-hexagon-scalable-content"
          style={{ transform: `scale(${item.scale || 1})` }}
        >
          <div className={`cb-hexagon-wrapper ${hexagonColorClass}`}>
            <div
              className="cb-hexagon-bg"
              style={{ backgroundImage: `url(${imageUrl})` }}
            />
            <div className="cb-hexagon-header">
              <span>B{bookNumber}</span>
              <span>{pageNumber}</span>
            </div>
            <div className="cb-hexagon-footer">{footerText}</div>
            <div className="cb-hexagon-content">
              <div className="cb-hexagon-title">
                {item.title.toUpperCase()}
              </div>
            </div>
          </div>
        </div>

        <button
          type="button"
          className="cb-hexagon-remove-btn"
          title="Verwijderen"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={() => onRemove(item.instanceId)}
        >
          ×
        </button>

        <div className="cb-hexagon-controls">
          <button
            type="button"
            className="cb-hexagon-scale-btn"
            title="Kleiner"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => onScaleChange(item.instanceId, 'decrease')}
          >
            -
          </button>
          <button
            type="button"
            className="cb-hexagon-scale-btn"
            title="Groter"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => onScaleChange(item.instanceId, 'increase')}
          >
            +
          </button>
        </div>
      </div>
    </Draggable>
  );
};

const ClusterBuilderPage = () => {
  const { doorloopjes, currentUser, loadInitialData } = useDoorloopjes();
  const [whys, setWhys] = useState([]);
  const [hows, setHows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState('');
  const [canvasItems, setCanvasItems] = useState([]);
  const [saveStatus, setSaveStatus] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);

  const canvasWrapperRef = useRef(null);
  const canvasRef = useRef(null);

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const hydratedRef = useRef(false);

  const [selectedId, setSelectedId] = useState(null);

  // Mobiel detectie
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined'
      ? window.matchMedia('(max-width: 768px)').matches
      : false
  );

  // --- Hooks altijd bovenaan; intern op isMobile checken ---
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mm = window.matchMedia('(max-width: 768px)');
    const handler = (e) => setIsMobile(e.matches);
    if (mm.addEventListener) mm.addEventListener('change', handler);
    else mm.addListener(handler);
    return () => {
      if (mm.removeEventListener) mm.removeEventListener('change', handler);
      else mm.removeListener(handler);
    };
  }, []);

  // Data ophalen (alleen desktop)
  useEffect(() => {
    if (isMobile) {
      setLoading(false);
      return;
    }
    const fetchExtraData = async () => {
      setLoading(true);
      const jwt = localStorage.getItem('jwt');
      const headers = { Authorization: `Bearer ${jwt}` };
      try {
        const [whyRes, howRes] = await Promise.all([
          axios.get(
            `${STRAPI_URL}/api/whies?populate[steps][populate]=image&populate=image`,
            { headers }
          ),
          axios.get(
            `${STRAPI_URL}/api/hows?populate[steps][populate]=image&populate=image`,
            { headers }
          ),
        ]);
        setWhys(whyRes.data.data);
        setHows(howRes.data.data);
      } catch (error) {
        console.error('Fout bij ophalen Why/How data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchExtraData();
  }, [isMobile]);

  // Hydrate cluster uit user-profiel (alleen desktop)
  useEffect(() => {
    if (
      isMobile ||
      hydratedRef.current ||
      !currentUser?.cluster_builder_state ||
      (doorloopjes.length === 0 && whys.length === 0 && hows.length === 0)
    ) {
      return;
    }

    const savedState = currentUser.cluster_builder_state;
    const allContent = [
      ...doorloopjes.map((d) => ({ ...d, __type: 'doorloopje' })),
      ...whys.map((w) => ({ ...w, __type: 'why' })),
      ...hows.map((h) => ({ ...h, __type: 'how' })),
    ];

    const reconstructedItems = savedState
      .map((savedItem) => {
        const fullItem = allContent.find(
          (c) => c.id === savedItem.id && c.__type === savedItem.__type
        );
        if (fullItem) {
          return {
            ...fullItem,
            x: savedItem.x,
            y: savedItem.y,
            instanceId: savedItem.instanceId || uuidv4(),
            scale: savedItem.scale || 1,
          };
        }
        return null;
      })
      .filter(Boolean);

    setCanvasItems(reconstructedItems);
    hydratedRef.current = true;
  }, [isMobile, currentUser, doorloopjes, whys, hows]);

  // Tap buiten item deselecteert (alleen desktop relevant, maar mag altijd luisteren)
  useEffect(() => {
    const onDocTap = (e) => {
      if (!selectedId) return;
      const el = e.target.closest?.('.cb-draggable-container');
      const isSame = el?.dataset?.instanceId === selectedId;
      if (!isSame) setSelectedId(null);
    };
    document.addEventListener('pointerdown', onDocTap, { passive: true });
    return () => document.removeEventListener('pointerdown', onDocTap);
  }, [selectedId]);

  // Viewport meta fix (kan altijd draaien)
  useEffect(() => {
    const meta = document.querySelector('meta[name="viewport"]');
    if (!meta) return undefined;
    const original = meta.getAttribute('content') || '';
    if (/\binteractive-widget=/.test(original)) return undefined;
    const patched = original
      ? `${original}, interactive-widget=overlays-content`
      : 'width=device-width, initial-scale=1, interactive-widget=overlays-content';
    meta.setAttribute('content', patched);
    return () => {
      meta.setAttribute('content', original);
    };
  }, []);

  // Scroll host gedrag (desktop)
  useEffect(() => {
    if (isMobile) return;
    const scrollHost = document.querySelector('.content-scroll-wrapper');
    if (!scrollHost) return;
    const prevOverflowY = scrollHost.style.overflowY;
    if (canvasItems.length === 0) {
      scrollHost.style.overflowY = 'hidden';
    } else {
      scrollHost.style.overflowY = '';
    }
    return () => {
      scrollHost.style.overflowY = prevOverflowY;
    };
  }, [isMobile, canvasItems.length]);

  // Body scroll klasses opruimen (veilig)
  useEffect(() => {
    document.body.classList.remove('no-page-scroll');
    document.documentElement.classList.remove('no-page-scroll');
    return () => {
      document.body.classList.remove('no-page-scroll');
      document.documentElement.classList.remove('no-page-scroll');
    };
  }, []);

  const filteredContent = useMemo(() => {
    if (isMobile) return [];
    if (!searchTerm) return [];
    const allContent = [
      ...doorloopjes.map((d) => ({ ...d, __type: 'doorloopje' })),
      ...whys.map((w) => ({ ...w, __type: 'why' })),
      ...hows.map((h) => ({ ...h, __type: 'how' })),
    ];
    return allContent
      .filter((item) =>
        item.title.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .slice(0, 15);
  }, [isMobile, searchTerm, doorloopjes, whys, hows]);

  const handleDragStop = (instanceId, data) => {
    setCanvasItems((prevItems) =>
      prevItems.map((item) =>
        item.instanceId === instanceId ? { ...item, x: data.x, y: data.y } : item
      )
    );
    setSaveStatus('');
  };

  const handleAddItemToCanvas = (contentItem) => {
    const wrapper = canvasWrapperRef.current;
    if (!wrapper) return;
    const newX =
      wrapper.scrollLeft + wrapper.clientWidth / 2 - HEXAGON_WIDTH / 2;
    const newY =
      wrapper.scrollTop + wrapper.clientHeight / 2 - HEXAGON_HEIGHT / 2;
    setCanvasItems((prevItems) => [
      ...prevItems,
      { ...contentItem, x: newX, y: newY, scale: 1, instanceId: uuidv4() },
    ]);
    setSaveStatus('');
    setIsSidebarOpen(false);
  };

  const handleScaleChange = (instanceId, direction) => {
    setCanvasItems((currentItems) =>
      currentItems.map((item) => {
        if (item.instanceId === instanceId) {
          const currentScale = item.scale || 1;
          const newScale =
            direction === 'increase'
              ? Math.min(MAX_SCALE, currentScale + SCALE_STEP)
              : Math.max(MIN_SCALE, currentScale - SCALE_STEP);
          return { ...item, scale: newScale };
        }
        return item;
      })
    );
    setSaveStatus('');
  };

  const handleRemoveItem = (instanceIdToRemove) => {
    setCanvasItems((prevItems) =>
      prevItems.filter((item) => item.instanceId !== instanceIdToRemove)
    );
    setSaveStatus('');
  };

  const handleSave = async () => {
    setSaveStatus('Opslaan...');
    const jwt = localStorage.getItem('jwt');
    if (!jwt || !currentUser) {
      setSaveStatus('Log in om je cluster op te slaan.');
      return;
    }
    const stateToSave = canvasItems.map((item) => ({
      id: item.id,
      __type: item.__type,
      x: item.x,
      y: item.y,
      scale: item.scale || 1,
      instanceId: item.instanceId,
    }));
    try {
      await axios.put(
        `${STRAPI_URL}/api/users/${currentUser.id}`,
        { cluster_builder_state: stateToSave },
        { headers: { Authorization: `Bearer ${jwt}` } }
      );
      await loadInitialData();
      setSaveStatus('✓ Opgeslagen!');
      setTimeout(() => setSaveStatus(''), 3000);
    } catch (error) {
      console.error('Fout bij opslaan cluster:', error);
      setSaveStatus('Opslaan lukte niet');
    }
  };

  const handleDownload = () => {
    if (!canvasRef.current) return;
    setIsDownloading(true);
    htmlToImage
      .toPng(canvasRef.current, {
        cacheBust: true,
        backgroundColor: '#f8f9fa',
        pixelRatio: 2,
      })
      .then((dataUrl) => {
        const link = document.createElement('a');
        link.download = 'mijn-cluster.png';
        link.href = dataUrl;
        link.click();
      })
      .catch((err) => {
        console.error('Oeps, er ging iets mis!', err);
      })
      .finally(() => {
        setIsDownloading(false);
      });
  };

  // --- Render ---
  if (isMobile) {
    return (
      <div className="detail-page-container" style={{ textAlign: 'center' }}>
        <h1>Clusterbouwer</h1>
        <p className="info-message" style={{ maxWidth: 680, margin: '0.75rem auto 1.25rem' }}>
          De Clusterbouwer is niet geschikt voor mobiele schermen.
          Open deze pagina op een laptop of desktop om je cluster samen te stellen.
        </p>
        <a href="/dashboard" className="btn-primary">Terug naar dashboard</a>
      </div>
    );
  }

  if (loading) return <p>Clusterbouwer laden...</p>;

  return (
    <div className="cluster-builder-page">
      <div
        className={`cb-overlay ${isSidebarOpen ? 'show' : ''}`}
        onClick={() => setIsSidebarOpen(false)}
      />
      <div className={`cb-sidebar ${isSidebarOpen ? 'show' : ''}`}>
        <div className="cb-header">
          <h2>Clusterbouwen</h2>
          <p>
            Met de Clusterbouwer zet je de doorloopjes die jij wilt gebruiken in je eigen plan.
          </p>
          <button
            type="button"
            className="cb-sidebar-close-btn"
            onClick={() => setIsSidebarOpen(false)}
          >
            ×
          </button>
        </div>

        <div className="cb-search">
          <input
            type="text"
            placeholder="Zoek doorloopje, waarom, hoe..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="cb-search-results">
          {filteredContent.map((item) => (
            <div
              key={`${item.__type}-${item.id}`}
              className="cb-search-result-item"
              onClick={() => handleAddItemToCanvas(item)}
            >
              <span className="cb-result-title">{item.title}</span>
              <span className="cb-result-type-label">
                {typeDisplayNames[item.__type] || item.__type}
              </span>
            </div>
          ))}
        </div>

        <div className="cb-actions">
          <button
            type="button"
            onClick={handleSave}
            className="btn-primary-cb"
            disabled={saveStatus === 'Opslaan...' || isDownloading}
          >
            {saveStatus || 'Cluster Opslaan'}
          </button>
          <button
            type="button"
            onClick={handleDownload}
            className="btn-secondary-cb"
            disabled={isDownloading}
          >
            {isDownloading ? 'Genereren...' : 'Download PNG'}
          </button>
        </div>
      </div>

      <div
        className="cb-canvas-wrapper"
        ref={canvasWrapperRef}
        style={{ overflow: canvasItems.length ? 'auto' : 'hidden' }}
      >
        <div className="cb-canvas" ref={canvasRef}>
          {canvasItems.map((item) => (
            <DraggableHexagon
              key={item.instanceId}
              item={item}
              onRemove={handleRemoveItem}
              onDragStop={handleDragStop}
              onScaleChange={handleScaleChange}
              onSelect={(id) => setSelectedId(id)}
              isSelected={selectedId === item.instanceId}
            />
          ))}
        </div>
      </div>

      <button
        type="button"
        className="cb-fab"
        onClick={() => setIsSidebarOpen(true)}
      >
        +
      </button>
    </div>
  );
};

export default ClusterBuilderPage;
